import { formatFullName } from '@/lib/utils'
import { formatCurrency } from '@/lib/currency-utils'
import { formatShortDate, formatTimestamp, timestampToDateOnly } from '@/lib/date-utils'
import type { Job, JobEventLite } from '@/gen/cortado/v1/jobs_pb'
import type { ActivityLite } from '@/gen/cortado/v1/actions_pb'
import type { InquiryEvent } from '@/gen/cortado/v1/inquiries_pb'
import type { InvoiceLite, QuoteEvent, QuoteLite } from '@/gen/cortado/v1/invoices_pb'
import {
  ActivityTypeEnum,
  EventStageEnum,
  InquiryStageEnum,
  InvoiceStatusEnum,
  JobEventSourceEnum,
  JobPaymentStateEnum,
  JobPhaseEnum,
  JobStatusEnum,
  QuoteStatusEnum,
} from '@/gen/cortado/v1/settings_pb'
import { formatEnumName, getEventStageColor, getQuoteStatusLabel, type BadgeVariant } from '@/lib/enum-utils'

export function jobDetailCustomerName(job: Job): string {
  return formatFullName(job.contactFirstName, job.contactLastName)
}

export function jobDetailInitials(job: Job): string {
  return `${job.contactFirstName?.[0] ?? ''}${job.contactLastName?.[0] ?? ''}`.toUpperCase() || '?'
}

export function jobDetailStatusVariant(status: JobStatusEnum): BadgeVariant {
  switch (status) {
    case JobStatusEnum.JOB_STATUS_OPEN:
      return 'info'
    case JobStatusEnum.JOB_STATUS_STALE:
      return 'warning'
    case JobStatusEnum.JOB_STATUS_LOST:
      return 'danger'
    case JobStatusEnum.JOB_STATUS_CLOSED:
      return 'secondary'
    default:
      return 'default'
  }
}

/** True when the job has a quote worth showing a Quote card for. */
export function jobDetailHasQuote(job: Job): boolean {
  return Boolean(job.latestQuoteNumber) || (job.quotedCents ?? 0n) > 0n
}

export function jobDetailEventTitle(event: JobEventLite): string {
  return event.eventTypeLabel || event.eventTypeKey || 'Event'
}

/** "Aug 14 · 40 guests · The Grand Hall" (guest count and location omitted when unset). */
export function jobDetailEventLine(event: JobEventLite): string {
  const parts: string[] = [event.eventDate ? formatShortDate(event.eventDate) : 'Date TBD']
  if (event.eventGuests != null) {
    parts.push(event.eventGuests === 1 ? '1 guest' : `${event.eventGuests} guests`)
  }
  if (event.eventLocation) parts.push(event.eventLocation)
  return parts.join(' · ')
}

export function jobDetailEventStageVariant(event: JobEventLite): BadgeVariant {
  return event.stage != null ? getEventStageColor(event.stage) : 'secondary'
}

export function jobDetailEventStageLabel(event: JobEventLite): string {
  return event.stage != null ? formatEnumName(event.stage, EventStageEnum) : 'Requested'
}

export type JobActivityIconKey = 'note' | 'email' | 'inquiry' | 'quote' | 'payment' | 'task' | 'default'

/** Which icon an activity row leads with, by its type. */
export function jobActivityIconKey(type: ActivityTypeEnum): JobActivityIconKey {
  switch (type) {
    case ActivityTypeEnum.ACTIVITY_TYPE_NOTE_ADDED:
      return 'note'
    case ActivityTypeEnum.ACTIVITY_TYPE_EMAIL_SENT:
    case ActivityTypeEnum.ACTIVITY_TYPE_EMAIL_RECEIVED:
      return 'email'
    case ActivityTypeEnum.ACTIVITY_TYPE_INQUIRY_CREATED:
    case ActivityTypeEnum.ACTIVITY_TYPE_INQUIRY_STAGE_CHANGED:
      return 'inquiry'
    case ActivityTypeEnum.ACTIVITY_TYPE_QUOTE_CREATED:
    case ActivityTypeEnum.ACTIVITY_TYPE_QUOTE_SENT:
    case ActivityTypeEnum.ACTIVITY_TYPE_QUOTE_VIEWED:
    case ActivityTypeEnum.ACTIVITY_TYPE_QUOTE_ACCEPTED:
    case ActivityTypeEnum.ACTIVITY_TYPE_QUOTE_REJECTED:
    case ActivityTypeEnum.ACTIVITY_TYPE_QUOTE_EXPIRED:
      return 'quote'
    case ActivityTypeEnum.ACTIVITY_TYPE_PAYMENT_RECEIVED:
    case ActivityTypeEnum.ACTIVITY_TYPE_PAYMENT_FAILED:
    case ActivityTypeEnum.ACTIVITY_TYPE_PAYMENT_REFUNDED:
      return 'payment'
    case ActivityTypeEnum.ACTIVITY_TYPE_TASK_CREATED:
    case ActivityTypeEnum.ACTIVITY_TYPE_TASK_COMPLETED:
      return 'task'
    default:
      return 'default'
  }
}

/** "JD" from "Jane Doe": the first letters of the first and last words, or '' for an empty name. */
export function jobDetailNameInitials(name?: string): string {
  const words = (name ?? '').trim().split(/\s+/).filter(Boolean)
  if (words.length === 0) return ''
  const letters = words.length === 1 ? words[0][0] : `${words[0][0]}${words[words.length - 1][0]}`
  return letters.toUpperCase()
}

/** An activity row's title, with " · <actor initials>" appended when it has an actor. */
export function jobActivityTitle(activity: ActivityLite): string {
  const initials = jobDetailNameInitials(activity.actorName)
  return initials ? `${activity.title} · ${initials}` : activity.title
}

/** Local midnight of `date`'s calendar day. Task due dates are date-only, so all day math is on local calendar days. */
export function startOfLocalDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate())
}

function addDays(date: Date, days: number): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate() + days)
}

/** Escapes plain text and wraps each line in a paragraph, the HTML shape notes are stored in. */
export function jobNoteTextToHtml(text: string): string {
  return text
    .trim()
    .split(/\r?\n/)
    .map((line) => `<p>${line.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')}</p>`)
    .join('')
}

/** Whether a note body is long enough to clamp behind "Show more". */
export function jobNoteIsLong(html: string): boolean {
  const lineCount = (html.match(/<\/p>|<br\s*\/?>|<\/li>|\n/g) ?? []).length
  return html.replace(/<[^>]*>/g, '').length > 280 || lineCount > 5
}

/** "just now", "5m ago", "2h ago", then the short date ("Aug 14") from 24 hours on. */
export function jobNoteTimeLabel(timestamp: { seconds: bigint } | undefined, now: Date = new Date()): string {
  if (!timestamp) return ''
  const elapsedMs = now.getTime() - Number(timestamp.seconds) * 1000
  const minutes = Math.floor(elapsedMs / 60_000)
  if (minutes < 1) return 'just now'
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  return formatShortDate(timestamp)
}

/** An open task's sub-line pieces: "due Aug 14" (flagged overdue when before today) and the assignee's initials. */
export interface JobTaskMetaParts {
  due?: string
  overdue: boolean
  assignee?: string
}

export function jobTaskMetaParts(
  dueDate: { seconds: bigint } | undefined,
  assigneeName?: string,
  now: Date = new Date(),
): JobTaskMetaParts {
  const dueDay = timestampToDateOnly(dueDate)
  return {
    due: dueDate ? `due ${formatShortDate(dueDate, 'UTC')}` : undefined,
    overdue: dueDay !== undefined && dueDay < startOfLocalDay(now),
    assignee: jobDetailNameInitials(assigneeName) || undefined,
  }
}

/** The local calendar day of the earliest event that has a date (events arrive date-sorted, unset last), in that event's timezone. */
export function jobEarliestEventDay(events: JobEventLite[]): Date | undefined {
  const first = events.find((event) => event.eventDate)
  if (!first?.eventDate) return undefined
  const [year, month, day] = formatTimestamp(first.eventDate, 'yyyy-MM-dd', first.eventTimezone || undefined)
    .split('-')
    .map(Number)
  if (!year || !month || !day) return undefined
  return new Date(year, month - 1, day)
}

export interface JobTaskDuePick {
  label: string
  date: Date
}

/**
 * The due-date popover's quick picks: Today, Tomorrow, Next week, and — when
 * the job has an event day — "Day before event" and "1 week before event",
 * each hidden when its date is already in the past.
 */
export function jobTaskDuePicks(now: Date, eventDay?: Date): JobTaskDuePick[] {
  const today = startOfLocalDay(now)
  const picks: JobTaskDuePick[] = [
    { label: 'Today', date: today },
    { label: 'Tomorrow', date: addDays(today, 1) },
    { label: 'Next week', date: addDays(today, 7) },
  ]
  if (eventDay) {
    const eventPicks = [
      { label: 'Day before event', date: addDays(startOfLocalDay(eventDay), -1) },
      { label: '1 week before event', date: addDays(startOfLocalDay(eventDay), -7) },
    ]
    picks.push(...eventPicks.filter((pick) => pick.date >= today))
  }
  return picks
}

/** "Today", "Tomorrow", or "Oct 8" for a due-date chip. */
export function jobTaskDueLabel(date: Date, now: Date): string {
  const today = startOfLocalDay(now)
  const day = startOfLocalDay(date)
  if (day.getTime() === today.getTime()) return 'Today'
  if (day.getTime() === addDays(today, 1).getTime()) return 'Tomorrow'
  return formatShortDate(day)
}

/** Quote statuses that mean a quote has gone out to the client (Revised is a sent quote that was edited). */
const SENT_QUOTE_STATUSES = new Set<QuoteStatusEnum>([
  QuoteStatusEnum.QUOTE_STATUS_SENT,
  QuoteStatusEnum.QUOTE_STATUS_VIEWED,
  QuoteStatusEnum.QUOTE_STATUS_REVISED,
])

/**
 * The inquiry stage a lead-phase job should read as. `SendQuote` moves the
 * inquiry to Quote Sent on a quote's first send and nothing moves it back, so
 * a lead whose sent quote was reverted to a draft, or deleted in favour of a
 * new draft, keeps a stored Quote Sent stage while no quote is out. That is
 * why the Lead step read "Quote Sent" with only a draft on the job. When none
 * of the job's quotes is sent, Quote Sent reads as In Progress. `quoteStatuses`
 * is undefined while the job's quotes load, which leaves the stage as stored.
 */
export function jobLeadDisplayStage(
  stage: InquiryStageEnum,
  quoteStatuses: QuoteStatusEnum[] | undefined,
): InquiryStageEnum {
  if (stage !== InquiryStageEnum.INQUIRY_STAGE_QUOTE_SENT || !quoteStatuses) return stage
  return quoteStatuses.some((status) => SENT_QUOTE_STATUSES.has(status))
    ? stage
    : InquiryStageEnum.INQUIRY_STAGE_IN_PROGRESS
}

const LEAD_STAGE_TITLES: Partial<Record<InquiryStageEnum, string>> = {
  [InquiryStageEnum.INQUIRY_STAGE_NEW]: 'New',
  [InquiryStageEnum.INQUIRY_STAGE_IN_PROGRESS]: 'In progress',
  [InquiryStageEnum.INQUIRY_STAGE_QUOTE_SENT]: 'Quote sent',
  [InquiryStageEnum.INQUIRY_STAGE_ACCEPTED]: 'Accepted',
  [InquiryStageEnum.INQUIRY_STAGE_REJECTED]: 'Rejected',
  [InquiryStageEnum.INQUIRY_STAGE_STALE]: 'Stale',
  [InquiryStageEnum.INQUIRY_STAGE_REFERRED]: 'Referred',
}

/** The newest draft among the job's quotes: the one "Open draft quote" opens. */
export function jobLatestDraftQuote<T extends Pick<QuoteLite, 'id' | 'quoteNumber' | 'status'>>(
  quotes: T[],
): T | undefined {
  return quotes
    .filter((quote) => quote.status === QuoteStatusEnum.QUOTE_STATUS_DRAFT)
    .reduce<T | undefined>((latest, quote) => (!latest || quote.quoteNumber > latest.quoteNumber ? quote : latest), undefined)
}

export interface JobLeadStageInput {
  /** The linked inquiry's stored stage; undefined while it loads or for a job with no inquiry. */
  inquiryStage?: InquiryStageEnum
  /** The job's quotes' statuses; undefined while they load. */
  quoteStatuses?: QuoteStatusEnum[]
}

/**
 * Stage 1 (Lead) stepper cell title. While the job is a lead or being quoted
 * it is the lead's own stage (see `jobLeadDisplayStage`). Once booked the
 * lead was accepted (booking is what accepts it); a job booked with no lead
 * at all says so.
 */
export function jobDetailLeadStageTitle(job: Job, input: JobLeadStageInput = {}): string {
  if (job.phase === JobPhaseEnum.JOB_PHASE_BOOKED || job.phase === JobPhaseEnum.JOB_PHASE_COMPLETE) {
    return job.inquiryId ? 'Accepted' : 'Booked directly'
  }
  const stage = input.inquiryStage !== undefined ? jobLeadDisplayStage(input.inquiryStage, input.quoteStatuses) : undefined
  return (stage !== undefined && LEAD_STAGE_TITLES[stage]) || 'In progress'
}

/**
 * The header's primary quote action. A lead with no quote drafts one; a lead
 * with a draft opens the newest draft. Everything else (a sent quote, a booked
 * or complete job, or quotes still loading) keeps the New quote menu.
 */
export type JobPrimaryQuoteAction =
  | { kind: 'draft' }
  | { kind: 'open-draft'; quoteId: string; quoteNumber: number }
  | { kind: 'new' }

export function jobPrimaryQuoteAction(
  job: Pick<Job, 'phase' | 'quoteIds'>,
  quotes: Pick<QuoteLite, 'id' | 'quoteNumber' | 'status'>[] | undefined,
): JobPrimaryQuoteAction {
  if (job.phase !== JobPhaseEnum.JOB_PHASE_NEW) return { kind: 'new' }
  if (job.quoteIds.length === 0) return { kind: 'draft' }
  const draft = quotes ? jobLatestDraftQuote(quotes) : undefined
  return draft ? { kind: 'open-draft', quoteId: draft.id, quoteNumber: draft.quoteNumber } : { kind: 'new' }
}

/**
 * Stage 2 (Quote) stepper cell title: the current quote's status (see
 * `jobCurrentQuote`), or "Quoted" while the quotes load. A job booked with no
 * quote in Cortado was quoted outside it (legacy bookings, and leads booked
 * without a quote); an unbooked job with no quote is "Not quoted".
 */
export function jobDetailQuoteStageTitle(
  job: Job,
  quotes: Pick<QuoteLite, 'id' | 'quoteNumber' | 'status'>[] | undefined,
): string {
  if (!jobDetailHasQuote(job)) return job.bookingId ? 'Quoted outside' : 'Not quoted'
  const current = jobCurrentQuote(job, quotes)
  return current ? getQuoteStatusLabel(current.status) : 'Quoted'
}

/**
 * The job's current quote: the one the Quote step names ("Quote #N" from
 * `latestQuoteNumber`), else the first in the Quotes card's order (accepted,
 * then newest). Undefined while the quotes load or when there are none.
 */
export function jobCurrentQuote<T extends Pick<QuoteLite, 'id' | 'quoteNumber'>>(
  job: Pick<Job, 'latestQuoteNumber' | 'acceptedQuoteId'>,
  quotes: T[] | undefined,
): T | undefined {
  if (!quotes || quotes.length === 0) return undefined
  const named = job.latestQuoteNumber ? quotes.find((quote) => quote.quoteNumber === job.latestQuoteNumber) : undefined
  if (named) return named
  const accepted = job.acceptedQuoteId ? quotes.find((quote) => quote.id === job.acceptedQuoteId) : undefined
  return accepted ?? quotes.reduce((newest, quote) => (quote.quoteNumber > newest.quoteNumber ? quote : newest))
}

export interface JobBookingTotals {
  invoiceCount: number
  invoicedCents: bigint
  paidCents: bigint
  dueCents: bigint
  /** Any invoice past its due date with money still owed. */
  hasOverdue: boolean
}

/** Money across the booking's invoices. Cancelled invoices don't count, as on the pipeline board. */
export function jobBookingTotals(invoices: InvoiceLite[]): JobBookingTotals {
  const live = invoices.filter((invoice) => invoice.status !== InvoiceStatusEnum.INVOICE_STATUS_CANCELLED)
  return {
    invoiceCount: live.length,
    invoicedCents: live.reduce((sum, invoice) => sum + invoice.totalCents, 0n),
    paidCents: live.reduce((sum, invoice) => sum + invoice.amountPaidCents, 0n),
    dueCents: live.reduce((sum, invoice) => sum + (invoice.amountDueCents > 0n ? invoice.amountDueCents : 0n), 0n),
    hasOverdue: live.some((invoice) => invoice.status === InvoiceStatusEnum.INVOICE_STATUS_OVERDUE),
  }
}

/** An invoice is the job's when the job lists it or it bills the job's booking. */
export function jobOwnsInvoice(job: Job, invoice: { id: string; bookingId: string }): boolean {
  if (job.invoiceIds.includes(invoice.id)) return true
  return Boolean(job.bookingId) && invoice.bookingId === job.bookingId
}

/** The booking's invoices, newest first, with cancelled ones sunk to the bottom. */
export function jobDetailSortInvoices(invoices: InvoiceLite[]): InvoiceLite[] {
  return [...invoices].sort((a, b) => {
    const aCancelled = a.status === InvoiceStatusEnum.INVOICE_STATUS_CANCELLED
    const bCancelled = b.status === InvoiceStatusEnum.INVOICE_STATUS_CANCELLED
    if (aCancelled !== bCancelled) return aCancelled ? 1 : -1
    return b.invoiceNumber - a.invoiceNumber
  })
}

/**
 * The next booked event that hasn't happened yet (today counts), from the
 * job's date-sorted events. Inquiry events and archived events are skipped.
 */
export function jobNextBookedEvent(events: JobEventLite[], now: Date = new Date()): JobEventLite | undefined {
  const today = startOfLocalDay(now)
  return events.find((event) => {
    if (event.source !== JobEventSourceEnum.JOB_EVENT_SOURCE_EVENT || event.archivedAt || !event.eventDate) return false
    const [year, month, day] = formatTimestamp(event.eventDate, 'yyyy-MM-dd', event.eventTimezone || undefined)
      .split('-')
      .map(Number)
    return Boolean(year && month && day) && new Date(year, month - 1, day) >= today
  })
}

/** The job's live booked events (inquiry and archived events left out), in the job's date order. */
export function jobBookedEvents(events: JobEventLite[]): JobEventLite[] {
  return events.filter((event) => event.source === JobEventSourceEnum.JOB_EVENT_SOURCE_EVENT && !event.archivedAt)
}

/** The last dated booked event, from the job's date-sorted events: when a complete job wrapped. */
export function jobLastBookedEvent(events: JobEventLite[]): JobEventLite | undefined {
  return jobBookedEvents(events).filter((event) => event.eventDate).at(-1)
}

/** The oldest invoice still owed money (cancelled ones left out): the one to chase first. */
export function jobOldestUnpaidInvoice(invoices: InvoiceLite[]): InvoiceLite | undefined {
  return invoices
    .filter((invoice) => invoice.status !== InvoiceStatusEnum.INVOICE_STATUS_CANCELLED && invoice.amountDueCents > 0n)
    .sort((a, b) => a.invoiceNumber - b.invoiceNumber)[0]
}

/**
 * Stage 3 (Booking) stepper cell title: where the money stands once the job is
 * booked. `totals` is undefined while invoices load.
 */
export function jobDetailBookingStageTitle(job: Job, totals: JobBookingTotals | undefined): string {
  if (!job.bookingId) return 'Not booked'
  if (!totals) return 'Booked'
  if (job.phase === JobPhaseEnum.JOB_PHASE_COMPLETE) return 'Complete'
  if (totals.invoiceCount === 0) return 'Not invoiced'
  if (totals.dueCents > 0n && (totals.hasOverdue || job.paymentState === JobPaymentStateEnum.JOB_PAYMENT_STATE_OVERDUE)) {
    return 'Payment overdue'
  }
  if (totals.dueCents <= 0n) return 'Paid in full'
  return totals.paidCents > 0n ? 'Deposit received' : 'Awaiting deposit'
}

/**
 * The header's primary action once the job is booked, following the Booking
 * step's sub-state (`jobDetailBookingStageTitle`):
 *
 * - Payment overdue: remind the client about the oldest overdue invoice.
 * - Awaiting deposit: send (a draft) or resend the oldest unpaid invoice.
 * - Deposit received / Paid in full / Not invoiced: open the next booked
 *   event. Deposit received with no upcoming event resends the invoice that
 *   still has a balance instead.
 * - Complete: book the same customer again.
 *
 * `none` while the invoices load, before booking, and for a cancelled (closed)
 * or lost booking.
 */
export type JobBookingPrimaryAction =
  | { kind: 'none' }
  | { kind: 'send-reminder'; invoiceId: string; invoiceNumber: number }
  | { kind: 'send-invoice'; invoiceId: string; invoiceNumber: number; isResend: boolean }
  | { kind: 'open-event'; eventId: string }
  | { kind: 'book-again' }

export function jobBookingPrimaryAction(
  job: Pick<Job, 'phase' | 'status' | 'bookingId' | 'contactId' | 'paymentState'>,
  invoices: InvoiceLite[] | undefined,
  nextEvent?: Pick<JobEventLite, 'id'>,
): JobBookingPrimaryAction {
  const none: JobBookingPrimaryAction = { kind: 'none' }
  if (job.phase === JobPhaseEnum.JOB_PHASE_COMPLETE) return job.contactId ? { kind: 'book-again' } : none
  if (job.phase !== JobPhaseEnum.JOB_PHASE_BOOKED || !job.bookingId || !invoices) return none
  if (job.status === JobStatusEnum.JOB_STATUS_CLOSED || job.status === JobStatusEnum.JOB_STATUS_LOST) return none

  const openEvent: JobBookingPrimaryAction = nextEvent ? { kind: 'open-event', eventId: nextEvent.id } : none
  const totals = jobBookingTotals(invoices)
  if (totals.invoiceCount === 0) return openEvent

  // Only invoices the client has been sent can be chased; a draft is sent, not reminded.
  const unpaid = invoices
    .filter((invoice) => invoice.status !== InvoiceStatusEnum.INVOICE_STATUS_CANCELLED && invoice.amountDueCents > 0n)
    .sort((a, b) => a.invoiceNumber - b.invoiceNumber)
  const isOverdue =
    totals.dueCents > 0n && (totals.hasOverdue || job.paymentState === JobPaymentStateEnum.JOB_PAYMENT_STATE_OVERDUE)
  if (isOverdue) {
    const overdue =
      unpaid.find((invoice) => invoice.status === InvoiceStatusEnum.INVOICE_STATUS_OVERDUE) ??
      unpaid.find((invoice) => invoice.status !== InvoiceStatusEnum.INVOICE_STATUS_DRAFT)
    if (overdue) return { kind: 'send-reminder', invoiceId: overdue.id, invoiceNumber: overdue.invoiceNumber }
  }

  if (totals.dueCents <= 0n) return openEvent
  if (totals.paidCents > 0n && nextEvent) return openEvent

  const invoice = unpaid[0]
  if (!invoice) return openEvent
  return {
    kind: 'send-invoice',
    invoiceId: invoice.id,
    invoiceNumber: invoice.invoiceNumber,
    isResend: invoice.status !== InvoiceStatusEnum.INVOICE_STATUS_DRAFT,
  }
}

/**
 * The job's quotes in selector order: the accepted quote pinned first, then
 * newest (highest quote number) first.
 */
export function jobDetailSortQuotes(quotes: QuoteLite[], acceptedQuoteId?: string): QuoteLite[] {
  return [...quotes].sort((a, b) => {
    if (acceptedQuoteId) {
      if (a.id === acceptedQuoteId) return -1
      if (b.id === acceptedQuoteId) return 1
    }
    return b.quoteNumber - a.quoteNumber
  })
}

/** "2026-06-01" — the event's calendar day in `timeZone` (viewer-local when unset), or "" when undated. */
function eventDayKey(timestamp: QuoteEvent['eventDate'], timeZone: string | undefined): string {
  return timestamp ? formatTimestamp(timestamp, 'yyyy-MM-dd', timeZone || undefined) : ''
}

/**
 * The inquiry event a quote event was drafted from: the one at the same
 * index, else the one on the same calendar day, else none. Quotes are drafted
 * from the inquiry in order, so index is the primary signal; the date match
 * covers a quote that has more events than the inquiry.
 */
export function jobDetailMatchInquiryEvent(
  index: number,
  quoteEvent: Pick<QuoteEvent, 'eventDate' | 'eventTimezone'>,
  inquiryEvents: InquiryEvent[],
): InquiryEvent | undefined {
  const byIndex = inquiryEvents[index]
  if (byIndex) return byIndex
  const quoteDay = eventDayKey(quoteEvent.eventDate, quoteEvent.eventTimezone)
  if (!quoteDay) return undefined
  return inquiryEvents.find(
    (inquiryEvent) => eventDayKey(inquiryEvent.eventDate, inquiryEvent.eventTimezone || quoteEvent.eventTimezone) === quoteDay,
  )
}

/**
 * The request (inquiry) event each quote event answers, in quote order. A quote
 * event names its source (`source_inquiry_event_id`) once it was drafted or
 * synced from the request, and is matched by that id alone: an event added on
 * the quote by hand, or one whose request event is gone, has no match. Only a
 * quote saved before events carried their source (none of its events names
 * one) falls back to `jobDetailMatchInquiryEvent`'s index-then-day guess.
 */
export function jobDetailMatchQuoteEvents(
  quoteEvents: Pick<QuoteEvent, 'eventDate' | 'eventTimezone' | 'sourceInquiryEventId'>[],
  inquiryEvents: InquiryEvent[],
): (InquiryEvent | undefined)[] {
  const isLinked = quoteEvents.some((event) => Boolean(event.sourceInquiryEventId))
  if (!isLinked) {
    return quoteEvents.map((event, index) => jobDetailMatchInquiryEvent(index, event, inquiryEvents))
  }
  const byId = new Map(inquiryEvents.map((event) => [event.id, event]))
  return quoteEvents.map((event) => (event.sourceInquiryEventId ? byId.get(event.sourceInquiryEventId) : undefined))
}

/** Quote event fields compared against the inquiry event they were drafted from. */
export type QuoteEventDiffField = 'eventDate' | 'eventLength' | 'eventGuests' | 'eventLocation' | 'baristaCount' | 'cartCount'

export type QuoteEventDiff = Record<QuoteEventDiffField, boolean>

const NO_QUOTE_EVENT_CHANGES: QuoteEventDiff = {
  eventDate: false,
  eventLength: false,
  eventGuests: false,
  eventLocation: false,
  baristaCount: false,
  cartCount: false,
}

/**
 * Which fields of a quote event differ from the inquiry event it was drafted
 * from. Strings compare trimmed, numbers treat unset and 0 as the same "not
 * set" value, and dates compare by calendar day, each in its own event
 * timezone (falling back to the other event's, then viewer-local). With no
 * inquiry event to compare against, nothing is marked changed.
 */
export function diffQuoteEventAgainstInquiry(quoteEvent: QuoteEvent, inquiryEvent: InquiryEvent | undefined): QuoteEventDiff {
  if (!inquiryEvent) return NO_QUOTE_EVENT_CHANGES
  const num = (value: number | undefined) => value || 0
  const str = (value: string | undefined) => (value ?? '').trim()
  return {
    eventDate:
      eventDayKey(quoteEvent.eventDate, quoteEvent.eventTimezone || inquiryEvent.eventTimezone) !==
      eventDayKey(inquiryEvent.eventDate, inquiryEvent.eventTimezone || quoteEvent.eventTimezone),
    eventLength: num(quoteEvent.eventLength) !== num(inquiryEvent.eventLength),
    eventGuests: num(quoteEvent.eventGuests) !== num(inquiryEvent.eventGuests),
    eventLocation: str(quoteEvent.eventLocation) !== str(inquiryEvent.eventLocation),
    baristaCount: num(quoteEvent.baristaCount) !== num(inquiryEvent.baristaCount),
    cartCount: num(quoteEvent.cartCount) !== num(inquiryEvent.cartCount),
  }
}

/** One "·"-separated part of a quote row's event summary. */
export interface QuoteEventSummaryToken {
  text: string
  /** Set only when this part differs from the request: "Request: 80 guests". */
  requestTooltip?: string
}

export interface QuoteEventsSummary {
  tokens: QuoteEventSummaryToken[]
  /** True when any event field, or the event count, differs from the request. */
  differsFromRequest: boolean
}

const DIFF_FIELD_LABEL: Record<QuoteEventDiffField, string> = {
  eventDate: 'date',
  eventLength: 'duration',
  eventGuests: 'guests',
  eventLocation: 'location',
  baristaCount: 'baristas',
  cartCount: 'carts',
}

/** "1 guest" / "25 guests". */
export function plural(count: number, noun: string): string {
  return `${count} ${noun}${count === 1 ? '' : 's'}`
}

/** "1 hr" / "2.5 hrs": the abbreviated event length. */
export function hoursText(hours: number): string {
  const rounded = parseFloat(hours.toFixed(2))
  return `${rounded} ${rounded === 1 ? 'hr' : 'hrs'}`
}

/** "Jun 14", "Jun 14 +1", or "Date TBD +1" for the earliest of `count` events. */
function firstDateText(event: { eventDate?: QuoteEvent['eventDate']; eventTimezone?: string } | undefined, count: number): string {
  if (count === 0 || !event) return 'No events'
  const day = event.eventDate ? formatShortDate(event.eventDate, event.eventTimezone || undefined) : 'Date TBD'
  return count > 1 ? `${day} +${count - 1}` : day
}

/** Chronological, TBD-dated last — the order event lists render in. */
function byEventDate(a: { eventDate?: { seconds: bigint } }, b: { eventDate?: { seconds: bigint } }) {
  if (!a.eventDate && !b.eventDate) return 0
  if (!a.eventDate) return 1
  if (!b.eventDate) return -1
  return Number(a.eventDate.seconds) - Number(b.eventDate.seconds)
}

/**
 * A quote's events as one summary line — "Jun 14 +1 · 6 hrs · 120 guests",
 * from its earliest event, with missing parts omitted — and how it differs
 * from the request (the inquiry's events).
 *
 * Each quote event is compared to the request event it was drafted from
 * (`jobDetailMatchQuoteEvents`, `diffQuoteEventAgainstInquiry`). A differing
 * date (or a different event count) highlights the date part, and differing
 * hours or guests on the earliest event highlight those parts. A difference no
 * visible part can carry — another event, location, baristas, carts, or a
 * value the quote leaves unset — adds a trailing "Details differ" part naming
 * the fields. With no request to compare against, nothing is highlighted.
 */
export function jobDetailQuoteEventsSummary(
  quoteEvents: QuoteEvent[],
  requestEvents: InquiryEvent[] | undefined,
): QuoteEventsSummary {
  const indexed = quoteEvents.map((event, index) => ({ event, index }))
  const first = [...indexed].sort((a, b) => byEventDate(a.event, b.event))[0]?.event

  const dateToken: QuoteEventSummaryToken = { text: firstDateText(first, quoteEvents.length) }
  const lengthToken: QuoteEventSummaryToken | undefined = first?.eventLength ? { text: hoursText(first.eventLength) } : undefined
  const guestsToken: QuoteEventSummaryToken | undefined = first?.eventGuests ? { text: plural(first.eventGuests, 'guest') } : undefined
  const tokens = [dateToken, lengthToken, guestsToken].filter((token): token is QuoteEventSummaryToken => Boolean(token))

  if (!requestEvents) return { tokens, differsFromRequest: false }

  const countDiffers = quoteEvents.length !== requestEvents.length
  let differsFromRequest = countDiffers
  const hidden = new Set<QuoteEventDiffField>()

  const matches = jobDetailMatchQuoteEvents(quoteEvents, requestEvents)
  for (const { event, index } of indexed) {
    const match = matches[index]
    if (!match) continue
    const diff = diffQuoteEventAgainstInquiry(event, match)
    for (const field of Object.keys(diff) as QuoteEventDiffField[]) {
      if (!diff[field]) continue
      differsFromRequest = true
      if (event === first && field === 'eventDate') {
        dateToken.requestTooltip = `Request: ${firstDateText(match, requestEvents.length)}`
      } else if (event === first && field === 'eventLength' && lengthToken) {
        lengthToken.requestTooltip = `Request: ${match.eventLength ? hoursText(match.eventLength) : 'not set'}`
      } else if (event === first && field === 'eventGuests' && guestsToken) {
        guestsToken.requestTooltip = `Request: ${match.eventGuests ? plural(match.eventGuests, 'guest') : 'not set'}`
      } else {
        hidden.add(field)
      }
    }
  }

  if (countDiffers && !dateToken.requestTooltip) {
    const requestFirst = [...requestEvents].sort(byEventDate)[0]
    dateToken.requestTooltip = `Request: ${firstDateText(requestFirst, requestEvents.length)}`
  }
  if (hidden.size > 0) {
    const labels = (Object.keys(DIFF_FIELD_LABEL) as QuoteEventDiffField[])
      .filter((field) => hidden.has(field))
      .map((field) => DIFF_FIELD_LABEL[field])
    tokens.push({ text: 'Details differ', requestTooltip: `Request has a different ${labels.join(', ')}` })
  }
  return { tokens, differsFromRequest }
}

/**
 * "$1,180.00 with tip" — what the customer pays at checkout when the quote has
 * an enforced tip (`total_with_tip_cents` above the tip-exclusive
 * `total_cents`); undefined without a tip.
 */
export function jobDetailWithTipText(totalCents: bigint, totalWithTipCents: bigint): string | undefined {
  if (totalWithTipCents <= totalCents) return undefined
  return `$${formatCurrency(Number(totalWithTipCents))} with tip`
}
